import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Delete, OpenInNew, PhotoLibrary } from '@mui/icons-material';
import { toast } from 'react-toastify';
import * as api from '../../services/api';
import { useI18n } from '../../i18n/useI18n';
import { rewriteToCdn } from '../../utils/cdn';
import { filterMachinesByAppProduct } from '../../utils/deviceMgmtFeatures';
import { userCardEnterSx } from './UserPageShell';
import DeviceFilterSelect from './DeviceFilterSelect';
import GalleryDateFilter from './GalleryDateFilter';
import PurgeOldGalleries from './PurgeOldGalleries';
import { GalleryPurgeBatch } from '../../utils/runGalleryYearPurge';

const PAGE_SIZE = 10;

export type LiteGalleryFilter = { machineCode?: string; startDate?: string; endDate?: string };

const toIso = (datetimeLocal: string): string => {
  if (!datetimeLocal) return '';
  const localDate = new Date(datetimeLocal);
  return Number.isNaN(localDate.getTime()) ? '' : localDate.toISOString();
};

export type FetchLitePage = (
  page: number,
  limit: number,
  filter?: LiteGalleryFilter
) => Promise<api.PaginatedResponse<api.LitePhotoGallery> | undefined>;

export interface LiteGalleryGridProps {
  fetchPage: FetchLitePage;
  onDelete?: (folderId: string) => Promise<unknown>;
  emptyMessage?: string;
  showDelete?: boolean;
  /** Chỉ hiện máy đúng product trong dropdown (lite / mono / pro) */
  deviceProduct?: 'lite' | 'mono' | 'pro';
  /** Loader danh sách máy (user hoặc admin) */
  loadMachines?: () => Promise<api.MachineInfo[]>;
  accentColor?: string;
  /** Xóa gallery từ 1 năm trở lên khi mở trang */
  purgeExpired?: () => Promise<GalleryPurgeBatch | undefined>;
  purgeExpiredKey?: string;
  purgeToast?: boolean;
  /** Vị trí dính của hàng lọc. Admin user detail nằm dưới tab Photos/Frames. */
  filterStickyTop?: { xs: number; sm: number };
}

const LiteGalleryGrid: React.FC<LiteGalleryGridProps> = ({
  fetchPage,
  onDelete,
  emptyMessage = 'Chưa có gallery M-Photo Lite.',
  showDelete = true,
  deviceProduct,
  loadMachines,
  accentColor = '#e86a8a',
  purgeExpired,
  purgeExpiredKey,
  purgeToast = false,
  filterStickyTop = { xs: 108, sm: 116 },
}) => {
  const { pages: t } = useI18n();
  const [groups, setGroups] = useState<api.LitePhotoGallery[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [machineCodeFilter, setMachineCodeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [allMachines, setAllMachines] = useState<api.MachineInfo[]>([]);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const filteredMachines = useMemo(() => {
    if (!deviceProduct) return allMachines;
    return filterMachinesByAppProduct(allMachines, deviceProduct);
  }, [allMachines, deviceProduct]);

  useEffect(() => {
    if (!loadMachines || !deviceProduct) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await loadMachines();
        if (!cancelled) setAllMachines(list || []);
      } catch (error) {
        console.error('Failed to load machines for lite gallery filter:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadMachines, deviceProduct]);

  const activeFilter = useMemo<LiteGalleryFilter | undefined>(() => {
    const startIso = toIso(startDate);
    const endIso = toIso(endDate);
    if (!machineCodeFilter && !startIso && !endIso) return undefined;
    return {
      ...(machineCodeFilter ? { machineCode: machineCodeFilter } : {}),
      ...(startIso ? { startDate: startIso } : {}),
      ...(endIso ? { endDate: endIso } : {}),
    };
  }, [machineCodeFilter, startDate, endDate]);

  const fetchFirstPage = useCallback(async () => {
    try {
      setLoading(true);
      setGroups([]);
      setPage(1);
      setHasMore(true);
      const result = await fetchPage(1, PAGE_SIZE, activeFilter);
      const data = result?.data || [];
      setGroups(data);
      const total = result?.pagination?.total ?? 0;
      setHasMore(data.length < total);
      setPage(2);
    } catch {
      toast.error('Không thể tải danh sách gallery Lite');
    } finally {
      setLoading(false);
    }
  }, [fetchPage, activeFilter]);

  const fetchNextPage = useCallback(
    async (nextPage: number) => {
      if (loadingMore) return;
      try {
        setLoadingMore(true);
        const result = await fetchPage(nextPage, PAGE_SIZE, activeFilter);
        const data = result?.data || [];
        setGroups((prev) => {
          const next = [...prev, ...data];
          const total = result?.pagination?.total ?? 0;
          setHasMore(next.length < total);
          return next;
        });
        setPage(nextPage + 1);
      } catch {
        toast.error('Không thể tải thêm gallery');
      } finally {
        setLoadingMore(false);
      }
    },
    [fetchPage, loadingMore, activeFilter]
  );

  useEffect(() => {
    fetchFirstPage();
  }, [fetchFirstPage]);

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchNextPage(page);
        }
      },
      { rootMargin: '240px' }
    );
    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }
    return () => observerRef.current?.disconnect();
  }, [hasMore, loadingMore, loading, page, fetchNextPage]);

  const handleDelete = async (folderId: string) => {
    if (!onDelete) return;
    try {
      await onDelete(folderId);
      setGroups((prev) => prev.filter((g) => g.folderId !== folderId));
      toast.success('Đã xóa gallery');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Xóa gallery thất bại');
    }
  };

  const deviceFilterUi = (
    <Box
      sx={{
        mb: 1.5,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1,
        position: { xs: 'sticky', md: 'static' },
        top: filterStickyTop,
        zIndex: 4,
        py: 0.5,
        bgcolor: 'background.default',
      }}
    >
      {deviceProduct && loadMachines ? (
        <DeviceFilterSelect
          value={machineCodeFilter}
          onChange={setMachineCodeFilter}
          machines={filteredMachines}
          accentColor={accentColor}
          fullWidth
          minWidth={0}
          sx={{ mb: 0, maxWidth: { xs: '100%', sm: 420 }, flex: '1 1 220px' }}
        />
      ) : null}
      <GalleryDateFilter
        startDate={startDate}
        endDate={endDate}
        onStartDate={setStartDate}
        onEndDate={setEndDate}
        onClear={() => {
          setStartDate('');
          setEndDate('');
        }}
        accentColor={accentColor}
      />
    </Box>
  );

  if (loading) {
    return (
      <Box>
        {deviceFilterUi}
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (groups.length === 0) {
    return (
      <Box>
        {deviceFilterUi}
        <Box
          sx={{
            py: 6,
            px: 2,
            textAlign: 'center',
            borderRadius: 3,
            bgcolor: 'grey.50',
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <PhotoLibrary sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            {machineCodeFilter || startDate || endDate
              ? 'Không có gallery nào khớp bộ lọc'
              : emptyMessage}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {purgeExpired ? (
        <PurgeOldGalleries
          purgeKey={purgeExpiredKey || 'lite'}
          toast={purgeToast}
          requestBatch={purgeExpired}
          onPurged={() => { void fetchFirstPage(); }}
        />
      ) : null}
      {deviceFilterUi}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            sm: 'repeat(3, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))',
            lg: 'repeat(5, minmax(0, 1fr))',
          },
          gap: 2,
        }}
      >
        {groups.map((group, index) => {
          const coverRaw = group.photos?.[0]?.url || '';
          const cover = rewriteToCdn(coverRaw);
          const created = group.createdAt
            ? new Date(group.createdAt).toLocaleString('vi-VN', { dateStyle: 'short', timeStyle: 'short' })
            : '';

          return (
            <Card
              key={group.folderId}
              elevation={0}
              sx={{
                borderRadius: 2.5,
                overflow: 'hidden',
                border: '1.5px solid',
                borderColor: alpha('#e86a8a', 0.42),
                bgcolor: '#fff',
                boxShadow: `0 1px 2px rgba(15,23,42,0.06), 0 8px 22px ${alpha('#e86a8a', 0.16)}`,
                ...userCardEnterSx(index),
                transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  borderColor: alpha('#e86a8a', 0.65),
                  boxShadow: `0 2px 4px rgba(15,23,42,0.08), 0 12px 28px ${alpha('#e86a8a', 0.24)}`,
                },
              }}
            >
              <Box sx={{ position: 'relative', bgcolor: alpha('#ff8ba7', 0.14) }}>
                {cover ? (
                  <CardMedia
                    component="img"
                    image={cover}
                    alt={group.folderId}
                    loading="lazy"
                    sx={{
                      aspectRatio: '3 / 4',
                      objectFit: 'contain',
                      width: '100%',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      aspectRatio: '3 / 4',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PhotoLibrary sx={{ fontSize: 40, color: alpha('#e86a8a', 0.45) }} />
                  </Box>
                )}
                <Chip
                  size="small"
                  label={`${group.photos?.length || 0} ảnh · ${group.printCount || 0} lượt in`}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    height: 22,
                    fontWeight: 800,
                    fontSize: '0.68rem',
                    bgcolor: alpha('#fff', 0.92),
                    color: '#e86a8a',
                  }}
                />
              </Box>
              <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  noWrap
                  title={group.folderId}
                  sx={{ fontSize: '0.78rem' }}
                >
                  {group.folderId}
                </Typography>
                {created && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {created}
                  </Typography>
                )}
              </CardContent>
              <CardActions sx={{ px: 1, pt: 0, pb: 1, justifyContent: 'space-between' }}>
                <Button
                  size="small"
                  startIcon={<OpenInNew sx={{ fontSize: 16 }} />}
                  href={group.viewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.75rem' }}
                >
                  {t.common.view}
                </Button>
                {showDelete && onDelete && (
                  <IconButton size="small" color="error" onClick={() => void handleDelete(group.folderId)}>
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </CardActions>
            </Card>
          );
        })}
      </Box>

      <Box ref={sentinelRef} sx={{ height: 40, mt: 2 }} />
      {loadingMore && (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={28} />
        </Box>
      )}
      {!hasMore && groups.length > 0 && (
        <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mt: 1 }}>
          Đã tải hết {groups.length} gallery
        </Typography>
      )}
    </Box>
  );
};

export default LiteGalleryGrid;
